import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // reCAPTCHA v3 site key
  const RECAPTCHA_SITE_KEY = "6LfLsfgsAAAAALBoK5KTbCMvD0ntF7bQcZ0TmBOJ";

  // Load reCAPTCHA script dynamically only on login page
  useEffect(() => {
    // Check if already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }
    
    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      existingScript.onload = () => setRecaptchaLoaded(true);
      return;
    }
    
    // Load script dynamically
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA');
      setRecaptchaLoaded(true); // Allow login attempt anyway
    };
    document.head.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      // Don't remove script, it may be needed if user navigates back
    };
  }, []);

  const executeRecaptcha = async () => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error("reCAPTCHA not loaded"));
        return;
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: "login" })
          .then(resolve)
          .catch(reject);
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      // Get reCAPTCHA v3 token
      const captchaToken = await executeRecaptcha();
      
      const res = await fetch(API_URL + "/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, captcha_token: captchaToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login gagal");
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/20">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-[#d4a84b] to-[#b8942f] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <img src="/logo-pesona.png" alt="Pesona Indonesia" className="w-12 h-12 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">Wonderful Images Indonesia</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-100 flex items-center gap-2"><span>⚠️</span>{error}</div>}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Username</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-[#d4a84b] focus:border-[#d4a84b] transition"
              placeholder="" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-[#d4a84b] focus:border-[#d4a84b] transition"
              placeholder="••••••••" required />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-[#d4a84b] to-[#b8942f] hover:from-[#b8942f] hover:to-[#9a7a25] text-white font-semibold py-3 px-4 rounded-xl transition shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>Memverifikasi...</> : <><span>🔐</span>Masuk Dashboard</>}
          </button>
        </form>
        <p className="text-center text-slate-400 text-sm mt-6">Kementerian Pariwisata RI</p>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-md border border-slate-100 hover:shadow-lg hover:border-[#d4a84b]/30 transition-all duration-300 group">
      <div className="flex items-center gap-4">
        <div className={"w-14 h-14 rounded-2xl " + color + " flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform"}>{icon}</div>
        <div>
          <p className="text-slate-500 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-[#1e3a5f]">{value?.toLocaleString() || 0}</p>
        </div>
      </div>
    </div>
  );
}

function DashboardHome({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(API_URL + "/api/admin/dashboard?token=" + token)
      .then((res) => res.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div></div>;

  const stats = data?.stats || {};
  const getThumb = (url) => {
    if (!url) return "";
    if (url.includes("-thumb.") || url.includes("-medium.")) return url;
    if (!url.includes("kilatstorage")) return url;
    const parts = url.split(".");
    const ext = parts.pop();
    return parts.join(".") + "-thumb." + ext;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-800">Dashboard Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="📊" label="Total Artikel" value={stats.total_articles} color="bg-blue-100" />
        <StatCard icon="🖼️" label="Total Gambar" value={stats.total_images} color="bg-green-100" />
        <StatCard icon="👁️" label="Total Views" value={stats.total_views} color="bg-purple-100" />
        <StatCard icon="📥" label="Total Downloads" value={stats.total_downloads} color="bg-amber-100" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-4">Artikel Terbaru</h3>
          <div className="space-y-3">
            {data?.recent_articles?.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center gap-3">
                <img src={getThumb(a.thumbnail)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{a.title}</p>
                  <p className="text-xs text-slate-500">{a.province_name}</p>
                </div>
                <span className="text-xs text-slate-400">{a.total_view} views</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-4">Artikel per Provinsi</h3>
          <div className="space-y-2">
            {data?.by_province?.slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{p.name}</span>
                <span className="text-sm font-medium text-slate-800">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArticleList({ token, onEdit }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  const fetchArticles = useCallback(() => {
    setLoading(true);
    fetch(API_URL + "/api/admin/articles?token=" + token + "&page=" + page + "&limit=20&search=" + search)
      .then((res) => res.json())
      .then((d) => { setArticles(d.articles || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token, page, search]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin hapus artikel ini?")) return;
    await fetch(API_URL + "/api/admin/articles/" + id + "?token=" + token, { method: "DELETE" });
    fetchArticles();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const action = currentStatus ? "nonaktifkan" : "aktifkan";
    if (!window.confirm(`Yakin ${action} artikel ini?`)) return;
    try {
      const res = await fetch(API_URL + "/api/admin/articles/" + id + "/toggle-status?token=" + token, { method: "PATCH" });
      if (res.ok) {
        fetchArticles();
      } else {
        alert("Gagal mengubah status");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const getThumb = (url) => {
    if (!url) return "";
    if (url.includes("-thumb.") || url.includes("-medium.")) return url;
    if (!url.includes("kilatstorage")) return url;
    const parts = url.split(".");
    const ext = parts.pop();
    return parts.join(".") + "-thumb." + ext;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Daftar Artikel</h2>
        <button onClick={() => onEdit(null)} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium">+ Tambah Artikel</button>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <input type="text" placeholder="Cari artikel..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500" />
        </div>
        {loading ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-500 border-t-transparent mx-auto"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Artikel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Provinsi</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Kategori</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Views</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {articles.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={getThumb(a.thumbnail)} alt="" className="w-10 h-10 rounded object-cover" />
                        <span className="text-sm font-medium text-slate-800 max-w-xs truncate">{a.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{a.province_name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{a.category_name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-center">{a.total_view}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={"px-2 py-1 rounded-full text-xs " + (a.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>
                        {a.is_active ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleToggleStatus(a.id, a.is_active)} className={(a.is_active ? "text-orange-600 hover:text-orange-800" : "text-green-600 hover:text-green-800") + " text-sm mr-2"}>{a.is_active ? "Nonaktifkan" : "Aktifkan"}</button>
                      <button onClick={() => onEdit(a)} className="text-blue-600 hover:text-blue-800 text-sm mr-2">Edit</button>
                      <button onClick={() => handleDelete(a.id)} className="text-red-600 hover:text-red-800 text-sm">Hapus</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-sm text-slate-500">Total: {total} artikel</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Prev</button>
            <span className="px-3 py-1 text-sm">Page {page}</span>
            <button onClick={() => setPage(page + 1)} disabled={articles.length < 20} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArticleForm({ token, article, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: article?.title || "",
    content: article?.content || "",
    id_province: article?.id_province || "",
    id_city: article?.id_city || "",
    id_category: article?.id_category || 34,
    is_video: article?.is_video || false,
    video_url: article?.video_url || "",
    tags_csv: article?.tags_csv || "",
    thumbnail: article?.thumbnail || "",
    posting_date: article?.posting_date || new Date().toISOString().split("T")[0],
  });
  const [provinces, setProvinces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [pendingGalleryImages, setPendingGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [deletingImage, setDeletingImage] = useState(null);
  const [togglingImage, setTogglingImage] = useState(null);

  useEffect(() => {
    fetch(API_URL + "/api/admin/provinces?token=" + token).then((r) => r.json()).then(setProvinces);
    fetch(API_URL + "/api/admin/categories?token=" + token).then((r) => r.json()).then(setCategories);
  }, [token]);

  // Load article data when article changes (for edit mode)
  useEffect(() => {
    if (article) {
      setForm({
        title: article.title || "",
        content: article.content || "",
        id_province: article.id_province || "",
        id_city: article.id_city || "",
        id_category: article.id_category || 34,
        is_video: article.is_video || false,
        video_url: article.video_url || "",
        tags_csv: article.tags_csv || "",
        thumbnail: article.thumbnail || "",
        posting_date: article.posting_date || new Date().toISOString().split("T")[0],
      });
      
      // Load gallery images if article has ID
      if (article.id) {
        loadGalleryImages(article.id);
      }
    }
  }, [article]);

  const loadGalleryImages = async (articleId) => {
    try {
      const res = await fetch(API_URL + `/api/admin/articles/${articleId}?token=${token}`);
      const data = await res.json();
      
      // Update form with content from backend
      if (data.content !== undefined) {
        setForm(prev => ({ ...prev, content: data.content }));
      }
      
      // Update gallery images
      if (data.images) {
        setGalleryImages(data.images);
      }
    } catch (err) {
      console.error("Failed to load images:", err);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(API_URL + "/api/admin/upload?token=" + token, { method: "POST", body: formData });
      const data = await res.json();
      setForm({ ...form, thumbnail: data.url });
    } catch (err) { alert("Upload gagal"); }
    finally { setUploading(false); }
  };

  const handleUploadGallery = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (files.length > 20) {
      alert("Maksimal 20 file per upload");
      return;
    }
    
    setUploadingGallery(true);
    setUploadProgress({ current: 0, total: files.length });
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        // Upload to storage first (get URL)
        const res = await fetch(API_URL + "/api/admin/upload?token=" + token, {
          method: "POST",
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          // Add to pending gallery images (not saved to DB yet)
          setPendingGalleryImages(prev => [...prev, {
            tempId: Date.now() + i,
            url: data.url,
            thumb: data.thumb || data.url,
            filename: file.name
          }]);
        } else {
          alert(`Gagal upload ${file.name}`);
        }
      } catch (err) {
        console.error("Upload failed:", err);
        alert(`Gagal upload ${file.name}`);
      }
      
      setUploadProgress({ current: i + 1, total: files.length });
    }
    
    setUploadingGallery(false);
    // Clear input
    e.target.value = '';
  };

  const handleRemovePendingImage = (tempId) => {
    setPendingGalleryImages(prev => prev.filter(img => img.tempId !== tempId));
  };

  const handleDeleteImage = async (imageId) => {
    if (!confirm("Hapus gambar ini?")) return;
    
    setDeletingImage(imageId);
    try {
      const res = await fetch(API_URL + `/api/admin/images/${imageId}?token=${token}`, { method: "DELETE" });
      if (res.ok) {
        setGalleryImages(prev => prev.filter(img => img.id !== imageId));
      } else {
        alert("Gagal menghapus gambar");
      }
    } catch (err) {
      alert("Error menghapus gambar");
    } finally {
      setDeletingImage(null);
    }
  };

  const handleToggleVisibility = async (imageId) => {
    setTogglingImage(imageId);
    try {
      const res = await fetch(API_URL + `/api/admin/images/${imageId}/toggle-visibility?token=${token}`, { method: "PUT" });
      if (res.ok) {
        const data = await res.json();
        setGalleryImages(prev => prev.map(img => img.id === imageId ? {...img, is_hidden: data.is_hidden} : img));
      } else {
        alert("Gagal mengubah visibility gambar");
      }
    } catch (err) {
      alert("Error mengubah visibility gambar");
    } finally {
      setTogglingImage(null);
    }
  };


  const handleGenerateTags = async () => {
    if (!form.title) return alert("Isi judul terlebih dahulu");
    setAiLoading("tags");
    try {
      const res = await fetch(API_URL + "/api/admin/ai/generate-tags?token=" + token + "&title=" + encodeURIComponent(form.title), { method: "POST" });
      const data = await res.json();
      setForm({ ...form, tags_csv: data.tags });
    } catch (err) { alert("Gagal generate tags"); }
    finally { setAiLoading(""); }
  };

  const handleSuggestCategory = async () => {
    if (!form.title) return alert("Isi judul terlebih dahulu");
    setAiLoading("category");
    try {
      const res = await fetch(API_URL + "/api/admin/ai/suggest-category?token=" + token + "&title=" + encodeURIComponent(form.title) + "&tags=" + encodeURIComponent(form.tags_csv), { method: "POST" });
      const data = await res.json();
      setForm({ ...form, id_category: data.category_id });
      alert("AI menyarankan kategori: " + data.category_name);
    } catch (err) { alert("Gagal suggest kategori"); }
    finally { setAiLoading(""); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.thumbnail) return alert("Judul dan thumbnail wajib diisi");
    setSaving(true);
    try {
      // Prepare gallery URLs from pending images
      const galleryUrls = pendingGalleryImages.map(img => img.url);
      
      // Add gallery URLs to form data and convert empty strings to null for int fields
      const submitData = {
        ...form,
        id_province: form.id_province === "" ? null : parseInt(form.id_province),
        id_city: form.id_city === "" ? null : parseInt(form.id_city),
        id_category: parseInt(form.id_category),
        gallery_urls: galleryUrls
      };
      
      console.log("Submitting article:", submitData);
      
      const url = article ? API_URL + "/api/admin/articles/" + article.id + "?token=" + token : API_URL + "/api/admin/articles?token=" + token;
      const res = await fetch(url, { 
        method: article ? "PUT" : "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(submitData) 
      });
      
      console.log("Response status:", res.status);

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Save failed:", errorData);
        throw new Error(errorData.detail || "Gagal menyimpan artikel");
      }
      
      const result = await res.json();
      console.log("Save success:", result);
      
      alert("✅ Artikel berhasil disimpan!");
      onSave();
    } catch (err) { 
      console.error("Error saving article:", err);
      alert("❌ Gagal menyimpan artikel: " + err.message); 
    }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">{article ? "Edit Artikel" : "Tambah Artikel Baru"}</h2>
        <button onClick={onCancel} className="text-slate-500 hover:text-slate-700">← Kembali</button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 space-y-5">
        {/* 2 KOLOM: Thumbnail (kiri) & Gallery (kanan) */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* KOLOM KIRI: Thumbnail */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">📷 Thumbnail Gambar Utama *</label>
            <div className="space-y-3">
              {form.thumbnail ? (
                <img src={form.thumbnail} alt="" className="w-full h-48 object-cover rounded-lg border" />
              ) : (
                <div className="w-full h-48 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                  <span className="text-slate-400 text-sm">No image</span>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleUpload} className="text-sm w-full" />
              {uploading && <p className="text-sm text-amber-600">⏳ Uploading...</p>}
              <p className="text-xs text-slate-400">Format: JPG, PNG, WebP (Max 50MB)</p>
            </div>
          </div>
          
          {/* KOLOM KANAN: Gallery Images */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">🖼️ Gallery Gambar (Upload Banyak Sekaligus)</label>
            <div className="space-y-3">
              <input 
                type="file" 
                accept="image/*" 
                multiple 
                onChange={handleUploadGallery}
                className="text-sm w-full"
                id="gallery-upload"
              />
              {uploadingGallery && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    ⏳ Uploading {uploadProgress.current}/{uploadProgress.total} files...
                  </p>
                  <div className="w-full bg-amber-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-amber-500 h-2 rounded-full transition-all" 
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Preview Pending Gallery Images */}
              {pendingGalleryImages.length > 0 ? (
                <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs text-slate-600 mb-2">{pendingGalleryImages.length} gambar siap disimpan:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {pendingGalleryImages.map((img) => (
                      <div key={img.tempId} className="relative group">
                        <img 
                          src={img.thumb} 
                          alt="" 
                          className="w-full h-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemovePendingImage(img.tempId)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                  <p className="text-slate-400 text-xs">Belum ada gambar dipilih</p>
                </div>
              )}
              
              <p className="text-xs text-slate-400">
                💡 Pilih multiple files sekaligus, lalu klik <strong>Simpan Artikel</strong> untuk menyimpan semua gambar
              </p>
            </div>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Judul Artikel *</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="Contoh: Keindahan Pantai Kuta Bali" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tanggal Posting</label>
          <input type="date" value={form.posting_date} onChange={(e) => setForm({ ...form, posting_date: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
          <p className="text-xs text-slate-400 mt-1">Kosongkan untuk menggunakan tanggal hari ini (artikel baru)</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Deskripsi / Isi Artikel</label>
          <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            placeholder="Tulis deskripsi atau isi artikel di sini..." rows="8"></textarea>
          <p className="text-xs text-slate-400 mt-1">Jelaskan secara detail tentang destinasi, keunikan, atau informasi penting lainnya.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Provinsi</label>
            <select value={form.id_province} onChange={(e) => setForm({ ...form, id_province: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500">
              <option value="">Pilih Provinsi</option>
              {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
            <div className="flex gap-2">
              <select value={form.id_category} onChange={(e) => setForm({ ...form, id_category: parseInt(e.target.value) })}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <button type="button" onClick={handleSuggestCategory} disabled={aiLoading === "category"}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium disabled:opacity-50">
                {aiLoading === "category" ? "..." : "🤖 AI"}
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
          <div className="flex gap-2">
            <input type="text" value={form.tags_csv} onChange={(e) => setForm({ ...form, tags_csv: e.target.value })}
              placeholder="wisata, pantai, bali" className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500" />
            <button type="button" onClick={handleGenerateTags} disabled={aiLoading === "tags"}
              className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm font-medium disabled:opacity-50">
              {aiLoading === "tags" ? "..." : "🤖 AI Generate"}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Pisahkan dengan koma. Klik AI Generate untuk otomatis dari judul.</p>
        </div>
        <div className="bg-slate-50 p-4 rounded-lg">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_video} onChange={(e) => setForm({ ...form, is_video: e.target.checked })} className="rounded" />
            <span className="text-sm font-medium text-slate-700">Ini adalah video</span>
          </label>
          {form.is_video && (
            <input type="text" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="URL Video (YouTube/Vimeo)" className="w-full px-4 py-2 border border-slate-300 rounded-lg mt-3" />
          )}
        </div>
        
        {/* Gallery Images Section */}
        {article?.id && (
          <div className="border-t pt-5">
            <label className="block text-sm font-medium text-slate-700 mb-3">🖼️ Gallery Gambar Artikel</label>
            <p className="text-xs text-slate-500 mb-3">Upload beberapa gambar untuk ditampilkan di halaman detail artikel</p>
            
            {/* Upload Multiple Images */}
            <div className="mb-4">
              <input 
                type="file" 
                accept="image/*" 
                multiple 
                onChange={handleUploadGallery}
                className="text-sm"
                id="gallery-upload"
              />
              {uploadingGallery && (
                <p className="text-sm text-amber-600 mt-2">⏳ Uploading gambar...</p>
              )}
            </div>
            
            {/* Gallery Images Preview */}
            {galleryImages.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {galleryImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img 
                      src={img.thumbnail || img.image_url} 
                      alt="" 
                      className={`w-full h-32 object-cover rounded-lg border ${img.is_hidden ? "border-red-300 opacity-50" : "border-slate-200"}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteImage(img.id)}
                      disabled={deletingImage === img.id}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    >
                      {deletingImage === img.id ? "..." : "🗑️"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleVisibility(img.id)}
                      disabled={togglingImage === img.id}
                      className={`absolute top-2 left-2 ${img.is_hidden ? "bg-green-500 hover:bg-green-600" : "bg-yellow-500 hover:bg-yellow-600"} text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50`}
                      title={img.is_hidden ? "Tampilkan gambar" : "Sembunyikan gambar"}
                    >
                      {togglingImage === img.id ? "..." : (img.is_hidden ? "👁️" : "🙈")}
                    </button>
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      {img.is_hidden && <span className="bg-red-500 px-1 rounded mr-1">Hidden</span>}{img.total_download || 0} downloads
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <p className="text-slate-500 text-sm">Belum ada gambar gallery</p>
                <p className="text-xs text-slate-400 mt-1">Upload gambar untuk menambahkan gallery</p>
              </div>
            )}
          </div>
        )}
        
        {!article?.id && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tips:</strong> Simpan artikel terlebih dahulu untuk menambahkan gambar gallery
            </p>
          </div>
        )}
        
        <div className="flex gap-3 pt-4 border-t">
          <button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
            {saving ? "Menyimpan..." : "Simpan Artikel"}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Batal</button>
        </div>
      </form>
    </div>
  );
}


function ReportsPage({ token }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);

  const months = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const monthName = months.find(m => m.value === selectedMonth).label;
      const response = await fetch(
        API_URL + `/api/admin/reports/monthly?token=${token}&year=${selectedYear}&month=${selectedMonth}`
      );
      
      if (!response.ok) throw new Error("Failed to generate report");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_Wonderfulimage_${monthName}_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert("Gagal generate laporan: " + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-800">Laporan Bulanan</h2>
      
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">Generate Laporan PDF</h3>
        <p className="text-sm text-slate-600 mb-6">
          Download laporan bulanan dalam format PDF. Laporan berisi statistik artikel, views, downloads, 
          dan breakdown per provinsi & kategori.
        </p>
        
        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Bulan</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tahun</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={handleDownloadReport}
            disabled={downloading}
            className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {downloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Generating...
              </>
            ) : (
              <>
                📥 Download Laporan
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">📊 Isi Laporan</h4>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>✅ Cover page profesional dengan logo Kemenpar</li>
            <li>✅ Daftar Isi (Table of Contents)</li>
            <li>✅ Ringkasan Eksekutif (AI-generated)</li>
            <li>✅ Statistik utama dengan perbandingan</li>
            <li>✅ Top 10 artikel terpopuler</li>
            <li>✅ List artikel baru bulan ini</li>
          </ul>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
          <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">📈 Visualisasi & Charts</h4>
          <ul className="text-sm text-purple-800 space-y-2">
            <li>📉 Line Chart - Trend artikel 6 bulan terakhir</li>
            <li>🥧 Pie Chart - Distribusi per kategori</li>
            <li>📊 Bar Chart - Top provinsi by views</li>
            <li>📋 Tabel breakdown provinsi & kategori</li>
          </ul>
        </div>
      </div>

      <div className="bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 rounded-xl p-6">
        <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">🤖 AI-Powered Insights</h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/60 rounded-lg p-3">
            <p className="font-medium text-green-800 mb-1">Ringkasan Eksekutif</p>
            <p className="text-green-700 text-xs">Analisis otomatis performa bulan ini</p>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <p className="font-medium text-green-800 mb-1">Analisis Data</p>
            <p className="text-green-700 text-xs">Insight distribusi konten & trend</p>
          </div>
          <div className="bg-white/60 rounded-lg p-3">
            <p className="font-medium text-green-800 mb-1">Rekomendasi Strategis</p>
            <p className="text-green-700 text-xs">Saran pengembangan konten</p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <h4 className="font-semibold text-amber-900 mb-3">💡 Info Penting</h4>
        <p className="text-sm text-amber-800">
          Laporan menggunakan <strong>OpenAI GPT-4o-mini</strong> untuk generate narasi analisis dan rekomendasi. 
          PDF berisi <strong>9 halaman</strong> dengan watermark logo Kemenpar. 
          Proses generate memerlukan waktu <strong>10-20 detik</strong>.
        </p>
      </div>
    </div>
  );
}


export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [view, setView] = useState("dashboard");
  const [editArticle, setEditArticle] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem("admin_token");
    const savedUser = localStorage.getItem("admin_user");
    if (savedToken && savedUser) { setToken(savedToken); setUser(JSON.parse(savedUser)); }
  }, []);

  const handleLogin = (admin) => { setUser(admin); setToken(localStorage.getItem("admin_token")); };
  const handleLogout = () => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_user"); setUser(null); setToken(null); };
  const handleEdit = (article) => { setEditArticle(article); setView("form"); };
  const handleSave = () => { setEditArticle(null); setView("articles"); };

  if (!user) return <LoginPage onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-72 bg-gradient-to-b from-[#1e3a5f] to-[#152a47] text-white p-6 shadow-2xl">
        {/* Logo Section */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-[#d4a84b] to-[#b8942f] rounded-xl flex items-center justify-center shadow-lg">
              <img src="/logo-pesona.png" alt="Pesona Indonesia" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Admin Panel</h1>
              <p className="text-slate-400 text-xs">Wonderful Images</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="space-y-2">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-4">Menu Utama</p>
          <button onClick={() => { setView("dashboard"); setEditArticle(null); }}
            className={"w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 " + (view === "dashboard" ? "bg-gradient-to-r from-[#d4a84b] to-[#b8942f] shadow-lg" : "hover:bg-white/10")}>
            <span className="text-xl">📊</span>
            <span className="font-medium">Dashboard</span>
          </button>
          <button onClick={() => { setView("articles"); setEditArticle(null); }}
            className={"w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 " + (view === "articles" || view === "form" ? "bg-gradient-to-r from-[#d4a84b] to-[#b8942f] shadow-lg" : "hover:bg-white/10")}>
            <span className="text-xl">📝</span>
            <span className="font-medium">Artikel</span>
          </button>
          
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-4 pt-6">Lainnya</p>
          <button onClick={() => navigate("/")} className="w-full text-left px-4 py-3 rounded-xl hover:bg-white/10 transition-all flex items-center gap-3">
            <span className="text-xl">🌐</span>
            <span className="font-medium">Lihat Website</span>
          </button>
        </nav>
        
        {/* User Section */}
        <div className="absolute bottom-6 left-6 right-6">
          <div className="bg-white/10 rounded-2xl p-4 backdrop-blur">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#d4a84b] to-[#b8942f] rounded-full flex items-center justify-center text-lg">
                {user.name?.charAt(0)?.toUpperCase() || "A"}
              </div>
              <div>
                <p className="font-medium text-sm">{user.name}</p>
                <p className="text-xs text-slate-400">Administrator</p>
              </div>
            </div>
            <button onClick={handleLogout} className="w-full text-left px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500 transition-all text-red-300 hover:text-white text-sm flex items-center gap-2">
              <span>🚪</span>
              <span>Keluar</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="ml-72 p-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1e3a5f]">
            {view === "dashboard" && "📊 Dashboard"}
            {view === "articles" && "📝 Kelola Artikel"}
            {view === "form" && (editArticle ? "✏️ Edit Artikel" : "➕ Artikel Baru")}
          </h2>
          <p className="text-slate-500">
            {view === "dashboard" && "Selamat datang di panel admin Wonderful Images"}
            {view === "articles" && "Kelola semua artikel dan galeri foto pariwisata"}
            {view === "form" && (editArticle ? "Ubah informasi artikel yang sudah ada" : "Buat artikel baru untuk galeri")}
          </p>
        </div>
        {view === "dashboard" && <DashboardHome token={token} />}
        {view === "articles" && <ArticleList token={token} onEdit={handleEdit} />}
        {view === "form" && <ArticleForm token={token} article={editArticle} onSave={handleSave} onCancel={() => { setView("articles"); setEditArticle(null); }} />}
        {/* ReportsPage dipindah ke /laporan */}
      </div>
    </div>
  );
}
